/*
 * Fills the star summary beside the product title from the Google reviews app.
 *
 * The app (Trustify) has no theme block for a compact rating, but its app embed
 * publishes the shop's Google Business figures on every page as
 * window.trggGooglePlace ({ rating, reviews_number, ... }). Reading that keeps the
 * summary in step with Google on its own - nothing here is ever typed in by hand.
 *
 * Two wrinkles this handles:
 *
 * 1. window.trggGooglePlace is a cached snapshot the app refreshes on its own
 *    schedule, and it can fall behind the count its carousel renders further down the
 *    same page. Rather than show one figure and swap it for another, the stars go up
 *    straight away and the count stays hidden until it is settled - then it is set
 *    once and never touched again.
 *
 * 2. The count links down to those reviews. The section is found at click time, not
 *    at load - the app block can render late, and the section's id depends on the
 *    template. The href is always left intact so the browser can still handle the
 *    jump if none of the lookups match.
 */
(function () {
  var SELECTOR = '[data-google-rating]';

  // Only the carousel's own summary header, never the review cards: a Google review
  // card can carry its author's "Local Guide - 12 reviews" line, and with the
  // carousel auto-scrolling, a loose selector reads a different number every pass.
  var CAROUSEL_SUMMARY = '[class*="trgg-google-review-compact-rating"]';

  var RATING_MAX = 5;
  var TICK_MS = 250;
  var PLACE_TRIES = 20; // ~5s waiting for the app embed's data
  var COUNT_TRIES = 24; // ~6s waiting for the carousel's fresher count

  // Dawn draws a half star between .3 and .7, a full one above that.
  function decimalStep(rating) {
    var remainder = rating % 1;
    if (remainder >= 0.3 && remainder <= 0.7) return 0.5;
    return remainder > 0.7 ? 1 : 0;
  }

  // Two decimals at most, and no trailing zeros: 5 stays "5", 4.82 stays "4.82".
  function format(rating) {
    return String(Math.round(rating * 100) / 100);
  }

  // The carousel header reads "PIERZEL 5.0 60 reviews" once the app has drawn it.
  function carouselCount() {
    var host = document.querySelector(CAROUSEL_SUMMARY);
    if (!host) return null;
    var match = (host.textContent || '').replace(/,/g, '').match(/(\d+)\s*reviews?/i);
    return match ? Number(match[1]) : null;
  }

  // Tried in order: the id the block was given, a section whose id names Google or
  // reviews, then whichever section the app's own widget sits in.
  function findReviewsSection(hash) {
    var id = (hash || '').replace(/^#/, '');
    if (id) {
      var byId = document.getElementById(id);
      if (byId) return byId;
    }

    var sections = document.querySelectorAll('[id^="shopify-section-"]');
    for (var i = 0; i < sections.length; i++) {
      if (/google|review/i.test(sections[i].id)) return sections[i];
    }

    var widget = document.querySelector(CAROUSEL_SUMMARY);
    return widget ? widget.closest('[id^="shopify-section-"]') || widget : null;
  }

  function scrollToSection(target) {
    var header = document.querySelector('sticky-header, .section-header');
    var offset = header ? header.getBoundingClientRect().height : 0;
    var top = target.getBoundingClientRect().top + window.pageYOffset - offset - 16;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: top, behavior: reduced ? 'auto' : 'smooth' });
  }

  function wireLink(link) {
    if (link.dataset.wired) return;
    link.dataset.wired = 'true';
    link.addEventListener('click', function (event) {
      var target = findReviewsSection(link.getAttribute('href'));
      if (!target) return; // leave it to the browser rather than swallowing the click
      event.preventDefault();
      scrollToSection(target);
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  }

  function eachWrapper(fn) {
    Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), fn);
  }

  // Stars and score only - the rating is right from the first read, so this can show
  // immediately.
  function paintRating(rating) {
    eachWrapper(function (wrapper) {
      var star = wrapper.querySelector('.rating-star');
      if (star) {
        star.style.setProperty('--rating', Math.floor(rating));
        star.style.setProperty('--rating-max', RATING_MAX);
        star.style.setProperty('--rating-decimal', decimalStep(rating));
      }

      var image = wrapper.querySelector('.rating');
      if (image && wrapper.dataset.ratingLabel) {
        image.setAttribute('aria-label', wrapper.dataset.ratingLabel.replace('__RATING__', format(rating)));
      }

      var score = wrapper.querySelector('[data-google-rating-score]');
      if (score) {
        score.textContent = (format(rating) + ' ' + (wrapper.dataset.ratingSuffix || '')).trim();
      }

      wrapper.hidden = false;
    });
  }

  // Written once, when the number is settled. The count carries the link, so this is
  // also where it gets wired.
  function paintCount(count) {
    eachWrapper(function (wrapper) {
      var counter = wrapper.querySelector('[data-google-rating-count]');
      if (!counter) return;
      counter.textContent = '(' + count + ' ' + (wrapper.dataset.googleLabel || '') + ')';
      counter.hidden = false;
      if (counter.tagName === 'A') wireLink(counter);
    });
  }

  // Prefer the carousel's live figure; settle for the snapshot only once it is clear
  // the carousel is not coming.
  function resolveCount(fallback) {
    var tries = 0;
    (function tick() {
      var count = carouselCount();
      if (count !== null) {
        paintCount(count);
        return;
      }
      if (++tries > COUNT_TRIES) {
        paintCount(fallback);
        return;
      }
      setTimeout(tick, TICK_MS);
    })();
  }

  var placeTries = 0;
  (function boot() {
    var place = window.trggGooglePlace;
    if (place && place.rating) {
      var rating = Number(place.rating);
      if (!rating || rating <= 0) return;
      paintRating(rating);
      resolveCount(place.reviews_number);
      return;
    }
    if (++placeTries > PLACE_TRIES) return;
    setTimeout(boot, TICK_MS);
  })();
})();
